import { useState, useEffect, useMemo } from "react";

const NAVY = "#0D1B2A";
const NAVY_2 = "#152A40";
const ACCENT = "#5BBCB0";
const ACCENT_DARK = "#3D9B8F";

const T = {
  en: {
    verifying: "Verifying your email...",
    successTitle: "Email confirmed!",
    successSub: "Your account is now active. You can sign in.",
    errorTitle: "Verification failed",
    backToLogin: "Go to sign in",
    invalidToken: "Invalid or missing token.",
  },
  es: {
    verifying: "Verificando tu correo...",
    successTitle: "¡Correo confirmado!",
    successSub: "Tu cuenta ya está activa. Puedes iniciar sesión.",
    errorTitle: "No se pudo verificar",
    backToLogin: "Ir a iniciar sesión",
    invalidToken: "Token inválido o faltante.",
  },
};

export default function VerifyEmailPage({ lang, setLang }) {
  const t = T[lang] || T.es;
  const token = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get("token") || ""; }
    catch (e) { return ""; }
  }, []);

  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) { if (!cancelled) setStatus("error"); return; }
      try {
        const res = await fetch("/.netlify/functions/verify-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: token }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success) { setError(data.error || ("HTTP " + res.status)); setStatus("error"); return; }
        setStatus("success");
      } catch (e) {
        if (!cancelled) { setError(e.message || String(e)); setStatus("error"); }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, " + NAVY + " 0%, " + NAVY_2 + " 100%)",
      padding: "40px 20px",
      fontFamily: "'DM Sans', sans-serif",
      color: "#fff",
    }}>
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <div style={{ textAlign: "right", marginBottom: 16 }}>
          {["en", "es"].map((l) => (
            <button key={l} onClick={() => setLang(l)} style={{
              padding: "4px 10px", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.2)",
              background: lang === l ? ACCENT : "transparent",
              color: lang === l ? NAVY : "rgba(255,255,255,0.7)",
              cursor: "pointer", marginLeft: 6, fontSize: 11, fontWeight: 600,
            }}>{l.toUpperCase()}</button>
          ))}
        </div>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <svg width="80" height="80" viewBox="-55 -55 110 110">
            <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35" />
            <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill={ACCENT} letterSpacing="3">GFI</text>
            <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill={ACCENT_DARK} />
            <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill={ACCENT_DARK} letterSpacing="3">NEXUS</text>
          </svg>
        </div>

        {status === "verifying" && (
          <div style={{
            padding: 32, background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>{t.verifying}</div>
          </div>
        )}

        {status === "success" && (
          <div style={{
            padding: 32, background: "rgba(91,188,176,0.12)",
            border: "1px solid " + ACCENT, borderRadius: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: ACCENT, marginBottom: 8 }}>{t.successTitle}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 20 }}>{t.successSub}</div>
            <button onClick={() => { try { window.location.href = "/"; } catch (e) {} }} style={{
              width: "100%", padding: "12px", borderRadius: 8, border: "none",
              background: ACCENT, color: NAVY, fontSize: 14, fontWeight: 700,
              cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em",
            }}>{t.backToLogin}</button>
          </div>
        )}

        {status === "error" && (
          <div style={{
            padding: 32, background: "rgba(231,76,60,0.12)",
            border: "1px solid rgba(231,76,60,0.4)", borderRadius: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#FCA5A5", marginBottom: 8 }}>{t.errorTitle}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 20 }}>{error || t.invalidToken}</div>
            <button onClick={() => { try { window.location.href = "/"; } catch (e) {} }} style={{
              width: "100%", padding: "12px", borderRadius: 8, border: "none",
              background: ACCENT, color: NAVY, fontSize: 14, fontWeight: 700,
              cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em",
            }}>{t.backToLogin}</button>
          </div>
        )}
      </div>
    </div>
  );
}
